// src/ui/MemberTagEditor.jsx — give a member a role, title or nickname.
//
// 🟢 THE SAME FIELD THE PHONE WRITES: `chats/{id}.participantTags`, a flat
//    `{ uid: string }` map on the chat document. It is chat-wide rather than
//    per-viewer, so a tag set here is the tag every member sees, on every
//    client — the sync is Firestore itself, not a message anybody has to
//    deliver.
//
// 🟢 THE PREVIEW WEARS THE REAL STYLESHEET. It renders `.ap-preview` with this
//    chat's own appearance classes, so the bubble, the wallpaper, the corner
//    radius and the text size are literally the ones the conversation uses —
//    the same trick the appearance panel's preview uses. A preview that painted
//    its own approximation of the theme would be wrong the moment either side
//    changed, which is exactly what went wrong in the phone's version of this
//    screen.
//
// 🟥 AND IT PREVIEWS A *RECEIVED* BUBBLE, because that is the only kind that
//    can carry a name. A sender's name — and so their tag — is never drawn on
//    your own outgoing messages, on either client.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from './icons';
import Avatar from './Avatar';
import { apClasses, apStyle, loadAppearance } from '../services/appearance';
import { setMemberTag } from '../services/groupAdmin';
import { nameColor } from './nameColor';

const MAX = 30;

export default function MemberTagEditor({ chat, me, uid, name, profile, initialTag, onClose }) {
  const [tag, setTag] = useState(initialTag || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  /* this chat's own look, read once — the preview is a still life, not a live
     subscription to the appearance panel */
  const [ap] = useState(() => loadAppearance(chat.id));

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await setMemberTag(chat, me, uid, tag);
      onClose();
    } catch (e) {
      setErr(e?.message || 'That tag could not be saved.');
      setBusy(false);
    }
  }, [busy, chat, me, uid, tag, onClose]);

  const clean = tag.trim();

  return (
    <div className="mt-veil" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mt" role="dialog" aria-label={`Tag for ${name}`}>
        <div className="mt-head">
          <b>Member tag</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="mt-body">
          <p className="mt-hint">
            Share a role, title or nickname for {uid === me.uid ? 'yourself' : name}. It shows next
            to their name in this group, for everyone.
          </p>

          <label className="mt-field">
            <span>Tag</span>
            <input
              ref={inputRef}
              value={tag}
              onChange={(e) => setTag(e.target.value.slice(0, MAX))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
              placeholder="Ex. Manager, Parent, Coach"
              maxLength={MAX}
              aria-label="Member tag"
            />
            <i>{tag.length}/{MAX}</i>
          </label>

          <div className="mt-pv-label">Preview</div>
          {/* the real classes, so this is the theme rather than a drawing of it */}
          <div className={`ap-preview mt-pv ${apClasses(ap)}`} style={apStyle(ap)}>
            <div className="ap-pv-msgs">
              <div className="row in">
                <div className="bubble">
                  <div className="mt-pv-who">
                    <span className="sender-name" style={{ color: nameColor(uid) }}>{name}</span>
                    {clean && <span className="member-tag">{clean}</span>}
                  </div>
                  Hello! This is how my messages will look.
                  <span className="time">10:00</span>
                </div>
              </div>
            </div>
          </div>

          {err && <div className="mt-err">{err}</div>}
        </div>

        <div className="mt-foot">
          <span className="mt-who">
            <Avatar profile={profile} size="sm" myUid={me.uid} />
            <i>{name}</i>
          </span>
          <span className="mt-actions">
            {/* 🟢 an empty tag IS the removal — the phone stores `tag.trim()` and
                treats '' as none, so there is no separate delete to get wrong */}
            {initialTag ? (
              <button
                className="mt-btn"
                disabled={busy}
                onClick={() => { setTag(''); }}
              >Clear</button>
            ) : null}
            <button className="mt-btn primary" disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
